import React, { useEffect, useRef } from "react";
// Ensure modal styles are loaded whenever the component is used
import "../styles/data-display/modal.scss";
import { createPortal } from "react-dom";
import { useSpring, animated } from "@react-spring/web";
import Button from "./Button";
const modalRoot = typeof document !== "undefined" ? document.body : null;

export default function Modal({
  open,
  onClose,
  title = "Dialog",
  children,
  footer,
  variant = "",
  className = "",
}) {
  const overlayRef = useRef(null);
  const lastActiveRef = useRef(null);

  useEffect(() => {
    if (open) {
      lastActiveRef.current = document.activeElement;
      const prev = document.body.style.overflow;
      document.body.style.overflow = "hidden";
      return () => {
        document.body.style.overflow = prev;
        lastActiveRef.current?.focus?.();
      };
    }
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => e.key === "Escape" && onClose?.();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  const anim = useSpring({
    opacity: open ? 1 : 0,
    transform: open
      ? "translateY(0) scale(1)"
      : "translateY(var(--paddingSM)) scale(1)",
    config: { tension: 280, friction: 24 },
  });

  if (!open || !modalRoot) return null;

  const rootClassName = ["modal", variant ? `modal--${variant}` : "", className]
    .filter(Boolean)
    .join(" ");

  const content = (
    <animated.div
      className={rootClassName}
      aria-hidden={!open}
      style={{ opacity: anim.opacity }}
    >
      <div
        className="modal-overlay"
        ref={overlayRef}
        onMouseDown={(e) => {
          if (e.target === overlayRef.current) onClose?.();
        }}
      >
        <Button
          className="modal-close"
          onClick={onClose}
          iconOnly
          icon="close"
          aria-label="Close"
        />
        <animated.div
          className="modal-dialog"
          role="dialog"
          aria-modal="true"
          aria-labelledby={title ? "modal-title" : undefined}
          style={anim}
        >
          <div className="modal-content">
            {title && (
              <div className="modal-header">
                <h2 id="modal-title" className="modal-title">
                  {title}
                </h2>
              </div>
            )}
            <div className="modal-body">{children}</div>
            {footer && <div className="modal-footer">{footer}</div>}
          </div>
        </animated.div>
      </div>
    </animated.div>
  );

  return createPortal(content, modalRoot);
}
